import { useState, useMemo, useEffect } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import TuneIcon from "@mui/icons-material/Tune";
import { AgGridReact } from "ag-grid-react";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";

import Card, { CARD_HOVER_SX, CARD_HOVER_TRANSITION } from "@plannel/components/dashboard/DashboardCard";
import DashboardCardHeader from "@plannel/components/dashboard/DashboardCardHeader";
import { DashboardPage, DashboardTopBar } from "@plannel/components/dashboard/DashboardLayout";
import { SegToggle } from "@plannel/components/dashboard/DashboardToggle";
import DashboardLayoutModal from "@plannel/components/dashboard/DashboardLayoutModal";
import { gridLine, axisTick, tooltipProps } from "@plannel/components/dashboard/chartTokens";
import { AgStyleTooltipContent } from "@plannel/components/rechart/AgStyleTooltip";
import { dashboardDefaultColDef, dashboardGridSx, DASHBOARD_ROW_HEIGHT } from "@plannel/components/dashboard/dashboardGrid";
import { makeFmtMoney, fmtQty } from "@plannel/components/dashboard/dashboardFormat";
import useDashboardData from "@plannel/hooks/useDashboardData";
import CompactSelect from "@plannel/components/dashboard/CompactSelect";
import useDashboardP13n from "@plannel/hooks/useDashboardP13n";
import AbcXyzChip from "@plannel/components/aggrid/AbcXyzChip";
import ColorUtil from "@plannel/pages/utils/color-util";
import inventoryService from "@plannel/services/dashboard/inventory-service";
import reduxUtil from "@plannel/utils/redux-util";
import { useMenuAccess } from "@plannel/hooks/useMenuAccess";
import ZDate from "@plannel/utils/zdate";
import { FS } from "@plannel/utils/font-util";

// 카드 정의 — span(6열 기준)·행 높이. 기본 레이아웃 순서 = 이 배열 순서.
//   minH 는 행 트랙 minmax(minH px, minH fr) 로 쓰인다 → "최소 높이" 겸 "남는 세로 분배 가중치".
//   추이 행은 작게(132) 두어 컴팩트하게, 그리드/차트 행은 크게 두어 남는 공간을 더 가져가 데이터가 더 보이게.
const CARD_DEFS = [
  { key: "invTrend", span: 2, minH: 132 },
  { key: "salesTrend", span: 2, minH: 132 },
  { key: "doiTrend", span: 2, minH: 132 },
  { key: "shortage", span: 3, minH: 280 },
  { key: "excess", span: 3, minH: 280 },
  { key: "slowStock", span: 3, minH: 260 },
  { key: "deadStock", span: 3, minH: 260 },
];
const DEFAULT_LAYOUT = { order: CARD_DEFS.map((c) => c.key), hidden: {} };

// 시리즈 색은 반드시 ColorUtil.colorSet(i) 로 잡는다(Personal Settings 프리셋 반영).
//   S&OP 대시보드는 **상단 카드 색을 통일한다** — 카드마다 색을 달리하면 색이 지표를 뜻하는 것처럼 읽히는데,
//   실제로 의미를 갖는 건 아래 차트의 시리즈 색이다. 다른 S&OP 대시보드(KPI 카드)도 같은 규칙이다.
//   위험을 뜻하는 색(품절/과잉 셀 강조, 불용 재고)은 팔레트가 아니라 의미색(error/warning)을 그대로 쓴다.
const SERIES_COLOR_IDX = 0;

// ─────────────────────────────────────────
// 추이 카드(재고/판매/재고일수) — 시작·종료 값 + 하단 그라데이션 영역
// ─────────────────────────────────────────
const TrendCard = ({ title, data, fmt, name, startLabel, endLabel, gradientId, headerRight, drill }) => {
  const color = useMemo(() => ColorUtil.colorSet(SERIES_COLOR_IDX), []);
  /*
   * v 가 **null 이면 "데이터 없음"** 이다(재고·DOI 는 그 달 스냅샷이 없으면 서버가 null 을 준다).
   *   `?? 0` 으로 뭉개면 차트는 선을 끊는데 숫자만 "₩0" 으로 남아 재고가 바닥난 것처럼 읽힌다.
   */
  const startV = data[0]?.v;
  const endV = data[data.length - 1]?.v;
  const fmtOrDash = (v) => (v == null ? "-" : fmt(v));
  return (
    <>
      <DashboardCardHeader title={title} dense {...drill}>{headerRight}</DashboardCardHeader>
      <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1, flex: 1, minHeight: 0 }}>
        <Box sx={{ flexShrink: 0 }}>
          <Typography sx={{ fontSize: FS.CAPTION, color: "text.secondary" }}>{startLabel}</Typography>
          <Typography sx={{ fontSize: FS.H1, fontWeight: 700, color: "text.primary" }}>{fmtOrDash(startV)}</Typography>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, height: "100%" }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 6, right: 0, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={0.28} />
                  <stop offset="100%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              {/* 숨김 XAxis — 툴팁 헤더를 배열 인덱스가 아니라 기간(period, 예 2026-03)으로 매핑 */}
              <XAxis dataKey="period" hide />
              <Tooltip {...tooltipProps()} formatter={(v) => fmt(v)} content={<AgStyleTooltipContent />} />
              <Area type="monotone" dataKey="v" name={name} stroke={color} strokeWidth={1.5}
                fill={`url(#${gradientId})`} dot={false} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
        <Box sx={{ flexShrink: 0, textAlign: "right" }}>
          <Typography sx={{ fontSize: FS.CAPTION, color: "text.secondary" }}>{endLabel}</Typography>
          <Typography sx={{ fontSize: FS.H1, fontWeight: 700, color: "success.main" }}>{fmtOrDash(endV)}</Typography>
        </Box>
      </Box>
    </>
  );
};

// ─────────────────────────────────────────
// 경고 테이블(품절/과잉) — AG Grid
// ─────────────────────────────────────────
/*
 * 품목 축 컬럼 — 품목 계층 레벨이 실제로 도는 자리는 이 경고 테이블 둘뿐이다
 *   (추이 3장은 전 품목 합, 부진·불용 재고는 aging 버킷·거점 축이라 레벨과 무관).
 *   최하위 레벨 = 품목 상세(코드·명·ABC), 상위 레벨 = 계층 노드 한 컬럼(서버가 접어서 준다).
 *   헤더는 선택한 레벨 라벨(z_hrchy_config.name — 테넌트 config 값이라 번역 대상이 아니다).
 * ★ ABC 는 그룹에서 의미가 없어 뺀다(등급은 품목 속성이지 그룹 속성이 아니다).
 */
const itemAxisCols = (t, grouped, groupLabel) => (grouped
  ? [{ headerName: groupLabel, field: "itemGrp", flex: 1, minWidth: 140 }]
  : [
    { headerName: t("invColItemCd"), field: "itemCd", width: 84 },
    { headerName: t("invColItemNm"), field: "itemNm", flex: 1, minWidth: 96 },
    { headerName: t("invColAbc"), field: "abc", width: 74, cellRenderer: AbcXyzChip },
  ]);

const AlertGrid = ({ title, rows, currencyCd, lastColKey, lastColLabel, danger, drill, grouped, groupLabel, onRowDrill }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const fmtMoney = useMemo(() => makeFmtMoney(currencyCd), [currencyCd]);

  const columnDefs = useMemo(() => [
    { headerName: t("invColLoc"), field: "loc", flex: 1, minWidth: 96 },
    ...itemAxisCols(t, grouped, groupLabel),
    { headerName: t("invColStock"), field: "stockAmt", width: 88, type: "rightAligned",
      valueFormatter: (p) => fmtMoney(p.value) },
    { headerName: t("invColQty"), field: "qty", width: 66, type: "rightAligned",
      valueFormatter: (p) => (p.value ?? 0).toLocaleString() },
    { headerName: t("invColInvDays"), field: "invDays", width: 74, type: "rightAligned" },
    { headerName: lastColLabel, field: lastColKey, width: 80, type: "rightAligned",
      cellStyle: {
        backgroundColor: alpha(danger ? theme.palette.error.main : theme.palette.warning.main, 0.15),
        color: danger ? theme.palette.error.dark : theme.palette.warning.dark,
        fontWeight: 700, textAlign: "right",
      } },
  ], [t, theme, fmtMoney, lastColKey, lastColLabel, danger, grouped, groupLabel]);

  return (
    <>
      <DashboardCardHeader title={title} dense {...drill} />
      <Box className="ag-theme-balham" sx={dashboardGridSx(theme, !!onRowDrill)}>
        <AgGridReact
          theme="legacy"
          rowData={rows}
          columnDefs={columnDefs}
          defaultColDef={dashboardDefaultColDef}
          rowHeight={DASHBOARD_ROW_HEIGHT}
          headerHeight={32}
          suppressCellFocus
          /* 권한이 없거나 롤업 중이면 핸들러가 안 와서 커서도 안 바뀐다 */
          onRowClicked={onRowDrill ? (e) => onRowDrill(e.data) : undefined}
        />
      </Box>
    </>
  );
};

// ─────────────────────────────────────────
// 부진 재고 — 가로 막대(aging 버킷)
// ─────────────────────────────────────────
const SlowStockCard = ({ title, data, currencyCd, drill }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const barColor = useMemo(() => ColorUtil.colorSet(SERIES_COLOR_IDX), []);
  const fmtMoney = useMemo(() => makeFmtMoney(currencyCd), [currencyCd]);
  return (
    <>
      <DashboardCardHeader title={title} dense {...drill} />
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, left: 12, bottom: 16 }}>
            <CartesianGrid strokeDasharray="3 4" stroke={gridLine(theme)} />
            <XAxis type="number" tick={axisTick(theme)} tickFormatter={fmtMoney}
              label={{ value: t("invSlowAxisAmt"), position: "insideBottom", offset: -8, fontSize: FS.CAPTION, fill: theme.palette.text.secondary }} />
            <YAxis dataKey="bucket" type="category" tick={axisTick(theme)} width={54} />
            <Tooltip {...tooltipProps()} formatter={(v) => fmtMoney(v)} content={<AgStyleTooltipContent />} />
            <Bar dataKey="amt" name={t("invSlowAxisAmt")} fill={barColor} radius={[0, 3, 3, 0]} barSize={16} />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </>
  );
};

// ─────────────────────────────────────────
// 불용 재고 — 도넛(가용/불용) + 거점별 막대 (AMT/% 토글)
// ─────────────────────────────────────────
const DeadStockCard = ({ title, pie, byLoc, currencyCd, drill }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const fmtMoney = useMemo(() => makeFmtMoney(currencyCd), [currencyCd]);
  const [mode, setMode] = useState("AMT");
  const okColor = useMemo(() => ColorUtil.colorSet(SERIES_COLOR_IDX), []);   // 가용 = 재고 계열 팔레트색
  const badColor = theme.palette.error.main;                                    // 불용 = 의미색(위험)
  const isPct = mode === "%";
  const barData = isPct ? byLoc.PCT : byLoc.AMT;
  const yFmt = isPct ? (v) => `${v}%` : fmtMoney;
  const pieData = pie.map((d) => ({ ...d, label: d.name === "dead" ? t("invDead") : t("invAvailable") }));

  return (
    <>
      <DashboardCardHeader title={title} dense {...drill}>
        {/* 드릴 클릭 영역은 제목뿐이라 토글에 전파 차단이 필요 없다 */}
        <SegToggle value={mode} options={["AMT", "%"]} onChange={setMode} />
      </DashboardCardHeader>
      <Box sx={{ flex: 1, display: "flex", gap: 1.5, minHeight: 0 }}>
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", minHeight: 0 }}>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" outerRadius="85%" dataKey="value" nameKey="label"
                  stroke={theme.palette.background.paper} strokeWidth={2}>
                  {pieData.map((d) => <Cell key={d.name} fill={d.name === "dead" ? badColor : okColor} />)}
                </Pie>
                <Tooltip {...tooltipProps()} formatter={(v, n) => [`${v}%`, n]} content={<AgStyleTooltipContent />} />
              </PieChart>
            </ResponsiveContainer>
          </Box>
          <Box sx={{ display: "flex", gap: 1.5, mt: 0.5, justifyContent: "center", flexShrink: 0 }}>
            {[{ c: okColor, l: t("invAvailable") }, { c: badColor, l: t("invDead") }].map((it) => (
              <Box key={it.l} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: 0.4, bgcolor: it.c }} />
                <Typography sx={{ fontSize: FS.BODY, color: "text.primary" }}>{it.l}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
        <Box sx={{ flex: 2, minHeight: 0 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={barData} margin={{ top: 4, right: 8, left: -4, bottom: 28 }}>
              <CartesianGrid strokeDasharray="3 4" stroke={gridLine(theme)} vertical={false} />
              <XAxis dataKey="loc" tick={axisTick(theme)} angle={-30} textAnchor="end" height={44} interval={0} />
              <YAxis tick={axisTick(theme)} tickFormatter={yFmt} domain={isPct ? [0, 100] : ["auto", "auto"]} />
              <Tooltip {...tooltipProps()} formatter={(v, n) => [isPct ? `${v}%` : fmtMoney(v), n]} content={<AgStyleTooltipContent />} />
              <Bar dataKey="available" name={t("invAvailable")} stackId="d" fill={okColor} radius={[0, 0, 0, 0]} maxBarSize={26} />
              <Bar dataKey="dead" name={t("invDead")} stackId="d" fill={badColor} radius={[3, 3, 0, 0]} maxBarSize={26} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    </>
  );
};

// ─────────────────────────────────────────
// 메인
// ─────────────────────────────────────────
const InventoryDashboard = ({ viewName }) => {
  const { t } = useTranslation();
  const { isPermitted } = useMenuAccess();
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const [unit, setUnit] = useState(reduxViewState?.unit ?? "AMT");           // 재고·판매 추이 공통 단위
  const [locId, setLocId] = useState("");                                    // 거점 필터(전체 카드 재조회) — 기본은 항상 전체("")
  const [itemLevel, setItemLevel] = useState(reduxViewState?.itemLevel ?? "");  // 품목 계층 집계 레벨(경고 테이블에만 적용)
  const [layoutOpen, setLayoutOpen] = useState(false);
  const [drillHoverKey, setDrillHoverKey] = useState(null);                  // 제목 hover 중인 드릴 카드 key

  // locId(거점)·itemLevel(계층 레벨)만 재조회 — 단위 토글은 AMT/QTY 모두 받으므로 재조회 없음
  const { payload } = useDashboardData(inventoryService.get, {
    locId: locId || undefined,
    itemLevel: itemLevel || undefined,
  });

  const { layout, saveLayout } = useDashboardP13n(viewName, { defaultLayout: DEFAULT_LAYOUT });

  useEffect(() => {
    // 단위·계층 레벨만 저장 — 거점(locId)은 저장 안 함(새로 열면 항상 전체)
    const next = reduxUtil.createObjectState(viewName, { unit, itemLevel });
    reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState, ...next }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unit, itemLevel]);

  const fmtMoney = useMemo(() => makeFmtMoney(payload?.currencyCd), [payload]);

  const cardLabels = useMemo(() => ({
    invTrend: t("invTrendCard"),
    salesTrend: t("invSalesTrendCard"),
    doiTrend: t("invDoiTrendCard"),
    shortage: t("invShortageCard"),
    excess: t("invExcessCard"),
    slowStock: t("invSlowStockCard"),
    deadStock: t("invDeadStockCard"),
  }), [t]);

  if (!payload) return null;

  const cc = payload.currencyCd;
  const fmtBy = unit === "AMT" ? fmtMoney : fmtQty;
  const from = payload.period?.from ?? "";
  const to = payload.period?.to ?? "";

  // 거점 필터 옵션 — 맨 앞에 전체("") + 재고 fact 의 거점
  const locOptions = [{ value: "", label: t("allLocationLabel") }, ...(payload.locations ?? [])];

  /*
   * 품목 계층 레벨 셀렉터 — 레벨이 2개 이상일 때만 나온다(하나면 고를 게 없다).
   * 롤업이 도는 카드는 품절·과잉 경고 둘뿐이다.
   */
  const levelOptions = (payload.itemLevels ?? []).map((lv) => ({
    value: String(lv.value),
    label: lv.label || lv.cd || `${t("level")} ${lv.value}`,
  }));
  // 그룹 컬럼 헤더 = 선택 레벨 라벨. 서버가 해석한 itemLevel 기준(요청값이 아니라).
  const activeLevel = levelOptions.find((lv) => lv.value === String(payload.itemLevel ?? ""));
  const itemGroupLabel = activeLevel?.label || t("itemLevel");
  const grouped = !!payload.itemGrouped;

  // 카드 → 리포트 드릴스루 (skill §9): 대상 페이지가 읽는 state 키에 맞춰 심고 탭 전환.
  //   현재 거점 필터(locId→locCd) + 기간(period.from/to)을 파라미터로 전달.
  /*
   * 드릴 공통 — 대상 4곳이 **같은 모양**을 읽는다(`filter.locations[].locCd`, `period.start/end`).
   * 드릴이 늘면서 같은 계산이 복붙되던 걸 헬퍼로 뺐다. 대상별로 다른 건 reduxKey 와 실어 보낼 키뿐이다.
   */
  const drillLocations = () => {
    const sel = (payload.locations ?? []).find((l) => String(l.value) === String(locId));
    return sel ? [{ locCd: sel.cd, locName: sel.label }] : null;   // 전체(locId="")면 null = 필터 없음
  };
  const drillPeriod = () => {
    const [fy, fm] = (payload.period?.from ?? "").split("-").map(Number);
    const [ty, tm] = (payload.period?.to ?? "").split("-").map(Number);
    return { start: new ZDate(fy, fm - 1, 1, 0, 0, 0), end: new ZDate(ty, tm, 0, 23, 59, 59) };
  };
  const drillTo = (target, state) => {
    reduxDispatch(reduxUtil.removeViewState({ viewName: target }));   // 이전 필터 제거(주입값 확실 적용)
    reduxDispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(target, state)));
    reduxDispatch(reduxUtil.updateHistoryState(target));              // 탭 전환
  };

  const openSlowMoving = () => drillTo("SLOW_MOVING_INVENTORY", {
    filter: { locations: drillLocations(), category: null },
    period: drillPeriod(),
  });

  // 재고 추이 카드 → Monthly Inventory Trend (같은 지표의 상세 추이)
  const openMonthlyTrend = () => drillTo("INV_MON_TREND", {
    filter: { locations: drillLocations(), category: null },
    period: drillPeriod(),
  });

  /*
   * 결품·과잉 카드 → Inventory Exceptions.
   * ⚠️ 대상엔 **예외 유형(결품/과잉) 파라미터가 없다** — 두 카드가 같은 화면을 같은 상태로 연다.
   *    거점 필터·RP 버전은 전달되지만 유형 선택은 사용자가 대상 화면에서 한다.
   */
  const openInventoryExceptions = () => drillTo("INV_EXC", {
    rpVersion: payload.latestRpVersionId != null ? { id: payload.latestRpVersionId } : null,
    filter: { locations: drillLocations(), category: null },
  });

  /*
   * 경고 테이블 **행** → Inventory Exceptions (그 행의 거점·품목으로 좁혀서).
   *   제목 드릴은 카드 전체를 열지만, 행은 "이 조합을 보겠다" 는 뜻이라 조건을 더 심는다.
   *
   * ★ 거점은 **코드**로 심는다 — 화면에는 거점명만 보이지만 이름은 중복될 수 있어
   *   필터 키로 쓰면 위험하다. 그래서 응답 행에 `locCd` 를 같이 싣는다(화면엔 안 쓴다).
   * ★ 품목은 대상 화면의 `itemSearch` 로 간다 — 거기서 `params.item` 이 되어 단일 품목 필터가 된다
   *   (`extraItemCodes` 는 복수 선택용이라 비워 둔다).
   *
   * 🚫 **롤업 중에는 걸지 않는다** — 그룹 행엔 품목코드가 없다. 거점만 심으면 "품목을 눌렀는데
   *   그 품목이 아닌 목록" 이 열려 누른 것과 다른 결과가 된다(Supply Trend 와 같은 처리).
   */
  const openExceptionRow = (row) => drillTo("INV_EXC", {
    rpVersion: payload.latestRpVersionId != null ? { id: payload.latestRpVersionId } : null,
    filter: {
      locations: row?.locCd ? [{ locCd: row.locCd, locName: row.loc }] : drillLocations(),
      category: null,
    },
    itemSearch: row?.itemCd ?? "",
    extraItemCodes: [],
  });

  // Expired 카드 → Expired Inventory(RP 리포트): 거점 + 최신 RELEASED RP 버전 프리셋.
  // RpVersionFilter 는 id 로 매칭해 전체 버전객체를 찾으므로 { id } 만 심으면 된다. 없으면 null.
  const openExpired = () => drillTo("EXPIRED_INVENTORY", {
    rpVersion: payload.latestRpVersionId != null ? { id: payload.latestRpVersionId } : null,
    filter: { locations: drillLocations(), category: null },
  });

  // 카드 key → 드릴 핸들러 + 대상 리포트 이름 (추가 카드는 여기 등록)
  // 권한 밖 화면으로 가는 드릴은 링크를 만들지 않는다 — onDrill 이 없으면 카드 제목·KPI 가 평문이 된다.
  const drillIf = (target, fn) => (isPermitted(target) ? fn : undefined);

  const drillDefs = {
    invTrend: { onDrill: drillIf("INV_MON_TREND", openMonthlyTrend), drillTitle: t("menuIpMonthlyInventoryTrend") },
    shortage: { onDrill: drillIf("INV_EXC", openInventoryExceptions), drillTitle: t("menuRpInventoryExceptions") },
    excess: { onDrill: drillIf("INV_EXC", openInventoryExceptions), drillTitle: t("menuRpInventoryExceptions") },
    slowStock: { onDrill: drillIf("SLOW_MOVING_INVENTORY", openSlowMoving), drillTitle: t("menuIpSlowMovingInventory") },
    deadStock: { onDrill: drillIf("EXPIRED_INVENTORY", openExpired), drillTitle: t("menuRpExpiredInventory") },
  };

  // 카드 key → 렌더 함수. drill = { onDrill, drillTitle, onDrillHover } → 카드 제목이 링크가 된다.
  const renderCard = (key, drill) => {
    switch (key) {
      case "invTrend":
        return <TrendCard title={cardLabels.invTrend} name={cardLabels.invTrend} data={payload.invTrend?.[unit] ?? []} fmt={fmtBy}
          startLabel={from} endLabel={to} gradientId="invTrendGrad" drill={drill} />;
      case "salesTrend":
        return <TrendCard title={cardLabels.salesTrend} name={cardLabels.salesTrend} data={payload.salesTrend?.[unit] ?? []} fmt={fmtBy}
          startLabel={from} endLabel={to} gradientId="salesTrendGrad" />;
      case "doiTrend":
        return <TrendCard title={cardLabels.doiTrend} name={cardLabels.doiTrend} data={payload.doiTrend ?? []}
          fmt={(v) => `${Math.round(v)}${t("invDaysUnit")}`} startLabel={from} endLabel={to} gradientId="doiTrendGrad" />;
      case "shortage":
        return <AlertGrid title={cardLabels.shortage} rows={payload.shortageRows ?? []} currencyCd={cc}
          lastColKey="shortDays" lastColLabel={t("invColShort")} danger drill={drill}
          grouped={grouped} groupLabel={itemGroupLabel}
          onRowDrill={grouped ? undefined : drillIf("INV_EXC", openExceptionRow)} />;
      case "excess":
        return <AlertGrid title={cardLabels.excess} rows={payload.excessRows ?? []} currencyCd={cc}
          lastColKey="excessDays" lastColLabel={t("invColExcess")} drill={drill}
          grouped={grouped} groupLabel={itemGroupLabel}
          onRowDrill={grouped ? undefined : drillIf("INV_EXC", openExceptionRow)} />;
      case "slowStock":
        return <SlowStockCard title={cardLabels.slowStock} data={payload.slowStock ?? []} currencyCd={cc} drill={drill} />;
      case "deadStock":
        return <DeadStockCard title={cardLabels.deadStock} pie={payload.deadStock?.pie ?? []}
          byLoc={payload.deadStock?.byLoc ?? { AMT: [], PCT: [] }} currencyCd={cc} drill={drill} />;
      default:
        return null;
    }
  };

  const spanOf = (key) => CARD_DEFS.find((c) => c.key === key)?.span ?? 3;
  const minHOf = (key) => CARD_DEFS.find((c) => c.key === key)?.minH ?? 250;
  const visibleOrder = (layout?.order ?? DEFAULT_LAYOUT.order).filter((k) => !layout?.hidden?.[k]);

  // 6열 기준으로 카드를 실제 행으로 묶는다(span 누적, 6 초과 시 다음 행).
  const gridRows = visibleOrder.reduce((rows, key) => {
    const s = spanOf(key);
    const last = rows[rows.length - 1];
    if (!last || last.used + s > 6) rows.push({ keys: [key], used: s });
    else { last.keys.push(key); last.used += s; }
    return rows;
  }, []);
  // 각 행 = minmax(최소높이, 최소높이fr): 세로 여백이 남으면 fr 가 비례로 채워 흰 여백 제거,
  // 모자라면 하한으로 스크롤. 테이블 행(minH 큼)이 가중이 커서 남는 공간을 더 가져가 데이터가 더 보인다.
  const gridTemplateRows = gridRows
    .map((r) => { const m = Math.max(...r.keys.map(minHOf)); return `minmax(${m}px, ${m}fr)`; })
    .join(" ");

  return (
    <DashboardPage>
      <DashboardTopBar period={payload.period} pinViewName={viewName}>
        <CompactSelect displayEmpty value={locId} options={locOptions} onChange={setLocId} />
        {levelOptions.length > 1 && (
          <CompactSelect displayEmpty value={itemLevel} options={[{ value: "", label: t("itemLevel") }, ...levelOptions]}
            onChange={setItemLevel} minWidth={96} />
        )}
        <SegToggle value={unit} options={["AMT", "QTY"]} onChange={setUnit} />
        <IconButton size="small" onClick={() => setLayoutOpen(true)} sx={{ p: 0.4, color: "text.secondary" }}>
          <TuneIcon sx={{ fontSize: 17 }} />
        </IconButton>
      </DashboardTopBar>

      <Box sx={{
        flex: 1, minHeight: 0, overflow: "auto",
        display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gridTemplateRows,
        gridAutoFlow: "row dense", gap: 1.5,   // 행 트랙(fr)이 남는 세로를 채워 하단 흰 여백 제거
      }}>
        {visibleOrder.map((key) => {
          // 드릴 클릭 영역은 카드 전체가 아니라 **제목**(차트·그리드·토글 클릭과 충돌 방지).
          //   제목 hover 를 받아 카드에 CARD_HOVER_SX 를 입혀 KPI 카드와 같은 하이라이트를 낸다.
          const def = drillDefs[key];
          const drill = def && { ...def, onDrillHover: (on) => setDrillHoverKey(on ? key : null) };
          return (
            <Card key={key}
              sx={{
                gridColumn: `span ${spanOf(key)}`, minHeight: 0,
                transition: CARD_HOVER_TRANSITION,
                ...(drillHoverKey === key && CARD_HOVER_SX),
              }}>
              {renderCard(key, drill)}
            </Card>
          );
        })}
      </Box>

      <DashboardLayoutModal
        open={layoutOpen}
        onClose={() => setLayoutOpen(false)}
        cards={CARD_DEFS.map((c) => ({ key: c.key, label: cardLabels[c.key] }))}
        layout={layout}
        onSave={(next) => { saveLayout(next); setLayoutOpen(false); }}
      />
    </DashboardPage>
  );
};

export default InventoryDashboard;
